import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AgentAvatar from '../AgentAvatar';
import { FormSurface, FormProgressChip, useFormPalette } from './DynamicFormPrimitives';
import { useLiveFormProgress } from './useLiveFormProgress';

const AVATAR_SIZE = 28;
const AVATAR_RADIUS = '6px';
const TEXT_INDENT = '38px';

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
}

function resolveTitle(parsedArgs, parsedResult) {
  return (
    parsedResult?.form?.title ||
    parsedArgs?.formInstance?.title ||
    parsedArgs?.title ||
    'Dynamic Form'
  );
}

function resolveSubtitle(parsedArgs, parsedResult, hasToolError) {
  if (hasToolError) return 'Form generation failed. Open details for diagnostics.';
  if (parsedResult?.form?.description) return parsedResult.form.description;
  if (parsedArgs?.formInstance?.description) return parsedArgs.formInstance.description;
  if (parsedArgs?.description) return parsedArgs.description;
  return 'Form content is dynamic. Open in drawer to view the live version.';
}

function getAgentAvatarClickHandler(agentId, onOpenAgentSettings) {
  if (!agentId || typeof onOpenAgentSettings !== 'function') return undefined;
  return (event) => {
    event.stopPropagation();
    onOpenAgentSettings(agentId);
  };
}

function getClickableAgentAvatarSx(avatarClick) {
  if (!avatarClick) return {};
  return {
    cursor: 'pointer',
    transition: 'transform 140ms ease, box-shadow 140ms ease, filter 140ms ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(24, 24, 27, 0.2)',
      filter: 'brightness(0.98)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 1px 4px rgba(24, 24, 27, 0.16)',
    },
  };
}

export default function DynamicFormCard({
  args,
  result,
  agentName,
  agentId,
  grouped,
  onOpenForm = null,
  onOpenAgentSettings = null,
  lifecycleState,
}) {
  const parsedArgs = useMemo(() => parseMaybeJson(args), [args]);
  const parsedResult = useMemo(() => parseMaybeJson(result), [result]);
  const inferredFormId =
    parsedResult?.formInstanceId ||
    parsedResult?.form?.formInstanceId ||
    parsedArgs?.formInstanceId ||
    parsedArgs?.formInstance?.formInstanceId ||
    null;
  const toolError = parsedResult?.error || null;
  const toolErrors = Array.isArray(parsedResult?.errors) ? parsedResult.errors : [];
  const hasToolError = Boolean(toolError) || toolErrors.length > 0;
  const title = resolveTitle(parsedArgs, parsedResult);
  const subtitle = resolveSubtitle(parsedArgs, parsedResult, hasToolError);
  const snapshotProgressStatus =
    parsedResult?.form?.progressStatus ||
    parsedArgs?.formInstance?.progressStatus ||
    null;
  const { progressStatus, isSubmitted, isActive } = useLiveFormProgress(
    inferredFormId,
    snapshotProgressStatus
  );
  const isRunning = lifecycleState === 'running' || lifecycleState === 'partial';
  const shareUrl =
    parsedResult?.shareLink?.url ||
    parsedResult?.form?.shareLinks?.[0]?.url ||
    null;
  const canOpen = Boolean(inferredFormId && typeof onOpenForm === 'function');
  const canOpenFullPage = Boolean(shareUrl);
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);

  const palette = useFormPalette();

  const handleOpen = () => {
    if (!canOpen) return;
    onOpenForm({
      formId: inferredFormId,
      args,
      result,
      title,
      progressStatus,
    });
  };

  const cardSubtitle = hasToolError
    ? subtitle
    : isRunning
      ? 'Creating form…'
      : isSubmitted
        ? 'Submitted'
        : isActive
          ? 'Awaiting submission'
          : subtitle;

  return (
    <Box sx={{ px: 2.5, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box sx={{ pl: grouped ? TEXT_INDENT : 0, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        {!grouped && (
          <AgentAvatar agentId={agentId} name={agentName} size={AVATAR_SIZE} onClick={avatarClick} />
        )}
        <Box sx={{ width: '75%', minWidth: '280px' }}>
          <FormSurface
            title={title}
            subtitle={cardSubtitle}
            rightSlot={
              <FormProgressChip progressStatus={progressStatus} isActive={isActive && !hasToolError} />
            }
          >
            {hasToolError && (
              <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {toolError && (
                  <Typography sx={{ fontSize: '12px', color: palette.errorText, fontWeight: 600 }}>
                    {toolError}
                  </Typography>
                )}
                {toolErrors.length > 0 && (
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {toolErrors.slice(0, 3).map((error, idx) => (
                      <Typography
                        component="li"
                        key={`${error}-${idx}`}
                        sx={{ fontSize: '11px', color: palette.errorText, lineHeight: 1.35 }}
                      >
                        {String(error)}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {inferredFormId ? (
              <Typography sx={{ fontSize: '12px', color: palette.textSecondary, mb: 1 }}>
                Form ID: <Box component="span" sx={{ fontFamily: 'monospace', color: palette.textPrimary }}>{inferredFormId}</Box>
              </Typography>
            ) : (
              <Typography sx={{ fontSize: '12px', color: palette.textSecondary, mb: 1 }}>
                Form ID unavailable in this event payload.
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={!canOpen}
                onClick={handleOpen}
                startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: 'none', fontSize: '12px' }}
              >
                Open form
              </Button>
              {canOpenFullPage && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => window.open(shareUrl, '_blank', 'noopener')}
                  startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                >
                  Full page
                </Button>
              )}
            </Box>
          </FormSurface>
        </Box>
      </Box>
    </Box>
  );
}
